import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Star, Plus, Trash2, Search, X, MapPin,
  Trophy, Award, AlertCircle, Loader
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProviderReferences = () => {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadReferences();
  }, []);

  const loadReferences = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/provider/references`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferences(response.data);
    } catch (error) {
      console.error('Error loading references:', error);
      toast.error('Fehler beim Laden der Referenzen');
    } finally {
      setLoading(false);
    }
  };

  const searchProviders = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/provider/references/search`, {
        params: { query: searchQuery },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching providers:', error);
      toast.error('Fehler bei der Suche');
    } finally {
      setSearching(false);
    }
  };

  const addReference = async (referenceProviderId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/provider/references/add`,
        { reference_provider_id: referenceProviderId },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { reference_provider_id: referenceProviderId }
        }
      );
      toast.success('Referenz erfolgreich hinzugefügt');
      loadReferences();
      setShowSearchModal(false);
      setSearchQuery('');
      setSearchResults([]);
    } catch (error) {
      console.error('Error adding reference:', error);
      const message = error.response?.data?.detail || 'Fehler beim Hinzufügen';
      toast.error(message);
    }
  };

  const removeReference = async (referenceId) => {
    if (!window.confirm('Möchten Sie diese Referenz wirklich entfernen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/provider/references/remove/${referenceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Referenz entfernt');
      loadReferences();
    } catch (error) {
      console.error('Error removing reference:', error);
      toast.error('Fehler beim Entfernen');
    }
  };

  const getPlanBadge = (planLevel) => {
    if (planLevel === 'pro') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold rounded-full">
          <Trophy className="w-3 h-3" />
          Top
        </span>
      );
    } else if (planLevel === 'plus') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-[#2B2142] to-[#3D3158] text-white text-xs font-bold rounded-full">
          <Award className="w-3 h-3" />
          Plus
        </span>
      );
    }
    return null;
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery) {
        searchProviders();
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader className="w-6 h-6 animate-spin text-yellow-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Top-Referenzen</h3>
            <p className="text-sm text-gray-600">
              Verlinken Sie Dienstleister, mit denen Sie erfolgreich zusammengearbeitet haben.
            </p>
          </div>
          <button
            onClick={() => setShowSearchModal(true)}
            disabled={references.length >= 3}
            className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <Plus className="w-4 h-4" />
            Hinzufügen
          </button>
        </div>

        {references.length >= 3 && (
          <div className="mt-3 p-3 bg-yellow-100 border border-yellow-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-yellow-800">
              Maximum 3 Referenzen erreicht. Entfernen Sie zuerst eine Referenz.
            </p>
          </div>
        )}
      </div>

      {/* Reference List */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">
          Meine Referenzen ({references.length}/3)
        </p>

        {references.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-xl">
            <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 text-sm mb-3">Noch keine Referenzen hinzugefügt</p>
            <button
              onClick={() => setShowSearchModal(true)}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors inline-flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              Erste Referenz hinzufügen
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {references.map((reference) => (
              <div
                key={reference.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:border-yellow-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <h4 className="font-bold text-gray-900">{reference.company_name}</h4>
                      {getPlanBadge(reference.plan_level)}
                    </div>

                    {reference.categories && reference.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {reference.categories.slice(0, 2).map((cat, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-yellow-50 text-yellow-700 rounded text-xs font-medium">
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}

                    {reference.location_city && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPin className="w-3 h-3" />
                        <span>{reference.location_city}</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => removeReference(reference.reference_id)}
                    className="text-red-600 hover:text-red-700 p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                    title="Referenz entfernen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Referenz hinzufügen</h3>
                <button
                  onClick={() => {
                    setShowSearchModal(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Search Bar */}
              <div className="relative">
                <Search className="hidden md:block absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Dienstleister suchen (Name, Firma)..."
                  className="w-full pl-4 md:pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                  autoFocus
                />
              </div>
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-5">
              {searching ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 animate-spin text-yellow-600" />
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  {searchQuery ? 'Keine Ergebnisse gefunden' : 'Geben Sie einen Suchbegriff ein'}
                </div>
              ) : (
                <div className="space-y-2">
                  {searchResults.map((provider) => (
                    <div
                      key={provider.id}
                      className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-bold text-gray-900 text-sm">{provider.company_name}</h4>
                            {getPlanBadge(provider.plan_level)}
                          </div>

                          {provider.categories && provider.categories.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-1">
                              {provider.categories.slice(0, 3).map((cat, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">
                                  {cat}
                                </span>
                              ))}
                            </div>
                          )}

                          {provider.location_city && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <MapPin className="w-3 h-3" />
                              <span>{provider.location_city}</span>
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => addReference(provider.user_id)}
                          className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm"
                        >
                          Hinzufügen
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderReferences;
